import { useUsersQuery } from "@/api/users.api";
import { Button } from "@/components/generic/Button";
import { Heading } from "@/components/generic/Heading";
import { Select, SelectItem } from "@/components/generic/Select";
import { TagField } from "@/components/generic/TagField";
import { TextField } from "@/components/generic/TextField";
import { useIntegrationSetup } from "@/components/integrations/IntegrationSetupContext";
import {
  useConnectIntegrationMutation,
  useIntegrationErrorDescriptionsQuery,
  useIntegrationQuery,
} from "@/queries/integrations";
import { fieldsFormSchema } from "@/types/fieldsFormSchema";
import {
  NotificationSettings,
  ProviderConnectionInfo,
  ProviderConnectionInfoFieldMapping,
  ProviderErrorDescription,
  ProviderErrorEnum,
} from "@/types/integrations";
import { Entries } from "@/types/utility";
import { mapExportInputs } from "@/utils/integration-helpers";
import { zodResolver } from "@hookform/resolvers/zod";
import { AnimatePresence } from "framer-motion";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "sonner";
import z from "zod";

const generateNotifcationsFormSchema = (errorTypes: ProviderErrorEnum[]) => {
  return z.object({
    notificationSettings: z.partialRecord(
      z.enum(errorTypes),
      z
        .object({
          notificationType: z.enum(["admins", "users", "emails"]),
          emails: z.string().nullable(),
          userIds: z.array(z.string()),
        })
        .superRefine((data, ctx) => {
          if (data.notificationType === "emails") {
            const emails = (data.emails ?? "")
              .toLowerCase()
              .split(",")
              .map((email) => email.trim());
            if (emails.length === 0) {
              ctx.issues.push({
                code: "too_small",
                minimum: 1,
                origin: "array",
                message: "Angiv mindst én email adresse",
                path: ["emails"],
                inclusive: true,
                input: emails,
              });
              return;
            }
            if (new Set(emails).size !== emails.length) {
              ctx.issues.push({
                code: "custom",
                message: "Duplikat email adresser er ikke tilladt",
                path: ["emails"],
                input: emails,
              });
              return;
            }
            for (let i = 0; i < (data.emails ?? "").length; i++) {
              const email = data.emails?.[i].toLowerCase();
              const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

              if (!email || !emailRegex.test(email)) {
                ctx.issues.push({
                  code: "invalid_format",
                  validation: "email",
                  format: "email",
                  message: `Ugyldig email adresse: ${email}`,
                  path: ["emails"],
                  input: email,
                });
              }
            }
          }
          if (data.notificationType === "users") {
            if (data.userIds.length === 0) {
              ctx.issues.push({
                code: "too_small",
                minimum: 1,
                origin: "array",
                message: "Angiv mindst én bruger",
                path: ["userIds"],
                inclusive: true,
                input: data.userIds,
              });
            }
          }
        }),
    ),
  });
};

export interface NotificationsStepProps {
  /** Called when the step completes successfully */
  onComplete?: (updatedConnectionInfo?: ProviderConnectionInfo) => void;
  /** Called when user goes back */
  onBack?: () => void;
  /** Pre-loaded error descriptions */
  errorDescriptionsInitialData?: Record<
    ProviderErrorEnum,
    ProviderErrorDescription
  >;
}

export function NotificationsStep({
  onComplete,
  onBack,
  errorDescriptionsInitialData,
}: NotificationsStepProps) {
  const {
    integration,
    mode,
    connectionInfo: connectionInfoAtomValue,
    setConnectionInfo,
    getSessionKey,
  } = useIntegrationSetup();

  const connectMutation = useConnectIntegrationMutation(
    mode === "setup" ? "post" : "put",
  );
  const { data: connectionInfoQuery } = useIntegrationQuery(integration);

  const connectionInfo = useMemo(() => {
    if (mode === "setup") {
      if (!connectionInfoAtomValue)
        throw new Error("Connection info is not set");
      return connectionInfoAtomValue;
    }
    return connectionInfoQuery;
  }, [connectionInfoQuery, connectionInfoAtomValue, mode]);

  const {
    data: errorDescriptions = errorDescriptionsInitialData ?? {},
    isLoading: isLoadingErrorDescriptions,
  } = useIntegrationErrorDescriptionsQuery(
    integration,
    connectionInfo ?? undefined,
  );

  const { data: users } = useUsersQuery({});

  const userOptions = useMemo(() => {
    if (!users) return [];
    return users?.pages
      .flatMap((p) => p.results)
      .map((user) => ({
        value: user.id,
        label: `${user.name} (${user.email})`,
      }));
  }, [users]);

  const notificationTypeOptions = [
    { key: "admins", value: "admins", label: "Alle administratorer" },
    { key: "users", value: "users", label: "Valgte brugere" },
    { key: "emails", value: "emails", label: "Email adresser" },
  ];

  const notificationsFormSchema = useMemo(() => {
    return generateNotifcationsFormSchema(
      Object.keys(errorDescriptions) as ProviderErrorEnum[],
    );
  }, [errorDescriptions]);

  const [hasResetForm, setHasResetForm] = useState(false);

  const notificationSettingsDefaults = useMemo<
    Required<z.input<typeof notificationsFormSchema>>
  >(() => {
    const notifications = (
      Object.keys(errorDescriptions) as ProviderErrorEnum[]
    ).reduce(
      (acc, errorType) => {
        const settings =
          connectionInfo?.settings?.notificationSettings?.[errorType];
        return {
          ...acc,
          [errorType]: {
            notificationType: settings?.emails.length
              ? ("emails" as const)
              : settings?.userIds.length
                ? ("users" as const)
                : ("admins" as const),
            emails: settings?.emails.join(", ") ?? "",
            userIds: settings?.userIds ?? [],
          },
        } satisfies z.input<
          typeof notificationsFormSchema
        >["notificationSettings"];
      },
      {} as z.input<typeof notificationsFormSchema>["notificationSettings"],
    );

    return {
      notificationSettings: notifications,
    };
  }, [errorDescriptions, connectionInfo]);

  const methods = useForm({
    mode: "onChange",
    resolver: zodResolver(notificationsFormSchema),
    defaultValues: notificationSettingsDefaults,
  });

  const {
    handleSubmit,
    control,
    formState: { isValid, errors },
    watch,
  } = methods;

  useEffect(() => {
    if (hasResetForm) return;
    if (!isLoadingErrorDescriptions && errorDescriptions) {
      methods.reset(notificationSettingsDefaults);
      setHasResetForm(true);
    }
  }, [
    errorDescriptions,
    hasResetForm,
    isLoadingErrorDescriptions,
    methods,
    notificationSettingsDefaults,
  ]);

  const formData = watch();

  useEffect(() => {
    if (errors.notificationSettings?.UnknownError) {
      console.error(
        "Unknown error in notification settings:",
        errors.notificationSettings.UnknownError,
      );
    }
    const result = notificationsFormSchema.safeParse(formData);
    if (!result.success) {
      console.error(
        "Form validation errors:",
        result.error,
        result.error.flatten(),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, errors]);

  const getNotificationType = (errorType: string) => {
    const settings = watch("notificationSettings");
    return (
      settings?.[errorType as ProviderErrorEnum]?.notificationType || "admins"
    );
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      if (mode === "update") {
        const notificationSettings = Object.entries(
          data.notificationSettings,
        ).reduce(
          (acc, [errorType, settings]) => ({
            ...acc,
            [errorType]: {
              emails:
                settings.notificationType === "emails"
                  ? (settings.emails
                      ?.toLowerCase()
                      .trim()
                      .split(",")
                      .map((email) => email.trim())
                      .filter((email) => email.length > 0) ?? [])
                  : [],
              userIds:
                settings.notificationType === "users"
                  ? (settings.userIds.filter((id) => id.length > 0) ?? [])
                  : [],
              sendToAdmins: settings.notificationType === "admins",
            },
          }),
          {} as Record<ProviderErrorEnum, NotificationSettings>,
        );
        const updatedConnectionInfo = {
          ...connectionInfo,
          settings: {
            ...connectionInfo?.settings,
            notificationSettings,
          },
        } as ProviderConnectionInfo;
        setConnectionInfo(updatedConnectionInfo);
        onComplete?.(updatedConnectionInfo);
        return;
      }

      // Gather all form data from previous steps
      const authData = JSON.parse(
        sessionStorage.getItem(getSessionKey("auth")) || "null",
      ) as Pick<
        ProviderConnectionInfo,
        "authType" | "accessKey" | "userName" | "password" | "apiUrl"
      >;
      const settingsData = JSON.parse(
        sessionStorage.getItem(getSessionKey("settings")) || "null",
      ) as Exclude<ProviderConnectionInfo["settings"], null>["other"];
      const fieldsData = JSON.parse(
        sessionStorage.getItem(getSessionKey("fields")) || "null",
        (_, value) => {
          if (typeof value === "object" && value !== null) {
            if (value.dataType === "Map" && Array.isArray(value.value)) {
              return new Map(value.value);
            }
          }
          return value;
        },
      ) as z.infer<typeof fieldsFormSchema>["fieldMappings"];

      // Rearrange the field mappings to match the expected format
      const exportMappings: ProviderConnectionInfoFieldMapping[] =
        mapExportInputs(fieldsData);

      // Transform form data to proper notification settings format
      const notificationSettings = {} as Record<
        ProviderErrorEnum,
        NotificationSettings
      >;

      Object.entries(data.notificationSettings).forEach(
        ([errorType, settings]) => {
          let emails: string[] = [];
          let userIds: string[] = [];
          let sendToAdmins = false;

          if (settings.notificationType === "emails") {
            emails =
              settings.emails
                ?.toLowerCase()
                ?.trim()
                ?.split(",")
                ?.map((email: string) => email.trim())
                .filter((email: string) => email.length > 0) ?? [];
          } else if (settings.notificationType === "users") {
            userIds =
              settings.userIds.filter((userId: string) => userId.length > 0) ??
              [];
          } else if (settings.notificationType === "admins") {
            sendToAdmins = true;
          }

          notificationSettings[errorType as ProviderErrorEnum] = {
            emails,
            userIds,
            sendToAdmins,
          };
        },
      );

      const completeFormData = {
        ...connectionInfo,
        ...authData,
        settings: {
          exportMappings: [
            ...(connectionInfo?.settings?.exportMappings ?? []),
            ...exportMappings,
          ].filter(
            (v, i, a) =>
              a.findIndex((t) => t.to.fieldId === v.to.fieldId) === i,
          ),
          other: { ...connectionInfo?.settings?.other, ...settingsData },
          notificationSettings: {
            ...connectionInfo?.settings?.notificationSettings,
            ...notificationSettings,
          },
        },
      } satisfies Partial<ProviderConnectionInfo>;

      // Submit the complete form data
      const promise = connectMutation.mutateAsync({
        integration: integration || "",
        data: completeFormData,
      });
      toast.promise(promise, {
        loading:
          mode === "setup"
            ? "Opretter integration..."
            : "Opdaterer integration...",
        success:
          mode === "setup" ? "Integration oprettet!" : "Integration opdateret!",
        error: () => ({
          closeButton: true,
          duration: Infinity,
          message: "Der opstod en fejl",
          description: "Kontakt support hvis problemet fortsætter.",
        }),
      });
      await promise;

      // Call completion handler — cleanup is handled by the context's onComplete
      onComplete?.();
    } catch (error) {
      console.error("Failed to connect integration:", error);
    }
  });

  const handleBack = () => {
    onBack?.();
  };

  if (isLoadingErrorDescriptions) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="animate-pulse">Indlæser...</p>
      </div>
    );
  }

  return (
    <AnimatePresence>
      {/* Form */}
      <form
        id="setupForm"
        onSubmit={onSubmit}
        className="flex flex-col gap-y-14"
      >
        <div className="flex flex-col gap-y-16">
          {(
            Object.entries(errorDescriptions) as Entries<
              Record<ProviderErrorEnum, ProviderErrorDescription>
            >
          ).map(([errorType, errorDescription]) => (
            <div key={errorType} className="flex flex-col justify-center">
              <Heading
                level={3}
                className="text-md mb-3 text-center font-medium"
              >
                {errorDescription.title}
              </Heading>
              <p className="text-theme-on-background-variant mb-10 text-center text-sm font-light">
                {errorDescription.description}
              </p>

              <div className="flex flex-col items-center justify-center gap-4 xl:flex-row">
                <div className="w-full xl:w-1/2">
                  <Controller
                    name={`notificationSettings.${errorType}`}
                    control={control}
                    render={({ field }) => {
                      const currentType = getNotificationType(errorType);

                      return (
                        <Select
                          aria-label="Vælg notifikationstype"
                          placeholder="Vælg notifikationstype"
                          selectedKey={currentType}
                          onSelectionChange={(type) => {
                            const currentValue = field.value as z.input<
                              typeof notificationsFormSchema
                            >["notificationSettings"][ProviderErrorEnum];

                            if (type === "admins") {
                              field.onChange({
                                notificationType: "admins",
                                emails: "",
                                userIds: [],
                              });
                            } else if (type === "users") {
                              field.onChange({
                                notificationType: "users",
                                emails: "",
                                userIds: currentValue?.userIds || [],
                              });
                            } else if (type === "emails") {
                              field.onChange({
                                notificationType: "emails",
                                emails: currentValue?.emails || "",
                                userIds: [],
                              });
                            }
                          }}
                          items={notificationTypeOptions}
                        >
                          {(item) => (
                            <SelectItem id={item.value}>
                              {item.label}
                            </SelectItem>
                          )}
                        </Select>
                      );
                    }}
                  />
                </div>

                {getNotificationType(errorType) !== "admins" && (
                  <div className="flex w-full items-center *:grow xl:w-1/2">
                    {getNotificationType(errorType) === "emails" && (
                      <TextField
                        name={`notificationSettings.${errorType}.emails`}
                        aria-label="Emails"
                        control={control}
                        data-1p-ignore
                        data-bwignore
                        placeholder="Emails (adskilt med komma)"
                      />
                    )}

                    {getNotificationType(errorType) === "users" && (
                      <Controller
                        name={`notificationSettings.${errorType}.userIds`}
                        control={control}
                        render={({ field }) => (
                          <TagField
                            {...field}
                            options={userOptions}
                            setSelectedOptions={(value) => {
                              if (Array.isArray(value)) {
                                field.onChange(value.map((item) => item.value));
                              }
                            }}
                            selectedOptions={userOptions.filter((u) =>
                              (field.value as string[]).includes(u.value),
                            )}
                            placeholder={
                              (field.value as string[]).length === 0
                                ? "Søg brugere"
                                : `${
                                    (field.value as string[]).length
                                  } bruger(e) valgt`
                            }
                            multiple
                          />
                        )}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Navigation buttons */}
        <div className="flex justify-center gap-x-4">
          {mode === "setup" && onBack && (
            <Button
              type="button"
              intent="secondary"
              onClick={handleBack}
              isDisabled={connectMutation.isPending}
            >
              Tilbage
            </Button>
          )}

          <Button
            type="submit"
            intent="primary"
            isDisabled={!isValid || connectMutation.isPending}
          >
            {connectMutation.isPending
              ? "Gemmer..."
              : mode === "update"
                ? "Gem ændringer"
                : "Gem og afslut"}
          </Button>
        </div>
      </form>
    </AnimatePresence>
  );
}
